/**
 * Web Sentry backend — implements ErrorMonitoringBackend for @sentry/react.
 * Sanitization and PostHog session linking are shared with mobile via @orb-labs/orby-react.
 */

import * as Sentry from "@sentry/react";
import {
  createPosthogSentrySessionLink,
  redactForMonitoring,
  sanitizeErrorMonitoringContext,
  sanitizeSentryEvent,
  type ErrorMonitoringBackend,
} from "@orb-labs/orby-react";

export function createWebSentryBackend(): ErrorMonitoringBackend {
  const dsn = import.meta.env.VITE_SENTRY_DSN as string | undefined;
  const environment = (import.meta.env.VITE_SENTRY_ENVIRONMENT as string) || "development";
  const posthogHost = import.meta.env.VITE_POSTHOG_HOST as string | undefined;
  const sessionLink = createPosthogSentrySessionLink(posthogHost);

  let isInitialized = false;

  if (dsn) {
    Sentry.init({
      dsn,
      environment,
      enabled: import.meta.env.PROD,
      tracesSampleRate: import.meta.env.PROD ? 0.2 : 0,
      sampleRate: 1.0,
      sendDefaultPii: false,
      ignoreErrors: ["Network request failed", "Failed to fetch", "AbortError", "ResizeObserver loop"],
      beforeSend(event) {
        sanitizeSentryEvent(event);
        sessionLink.mergeIntoEvent(event);
        return event;
      },
    });
    isInitialized = true;
  }

  return {
    captureException(error, context) {
      if (!isInitialized) return;
      const sanitized = sanitizeErrorMonitoringContext(context);
      Sentry.captureException(error, { extra: sanitized });
    },
    captureMessage(message, level, context) {
      if (!isInitialized) return;
      const sanitized = sanitizeErrorMonitoringContext(context);
      Sentry.captureMessage(redactForMonitoring(message), {
        level: (level ?? "info") as Sentry.SeverityLevel,
        extra: sanitized,
      });
    },
    identifyUser(userId, properties) {
      if (!isInitialized) return;
      const sanitized = properties ? sanitizeErrorMonitoringContext(properties) ?? {} : {};
      Sentry.setUser({ id: userId, ...sanitized });
    },
    clearUser() {
      if (!isInitialized) return;
      Sentry.setUser(null);
      sessionLink.clearLinkedSession();
    },
    setTag(key, value) {
      if (!isInitialized) return;
      Sentry.setTag(key, value);
    },
    addBreadcrumb(category, message, data) {
      if (!isInitialized) return;
      Sentry.addBreadcrumb({
        category,
        message: redactForMonitoring(message),
        data: data ? sanitizeErrorMonitoringContext(data) ?? undefined : undefined,
        level: "info",
      });
    },
    setSessionLink(sessionId, distinctId) {
      sessionLink.setSessionLink(sessionId, distinctId);
      if (isInitialized) {
        sessionLink.applyTagsIfActive((k, v) => Sentry.setTag(k, v));
      }
    },
  };
}
